import { useState, useEffect, useCallback } from 'react';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  action?: {
    label: string;
    url?: string;
    onClick?: () => void;
  };
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    // Tentar carregar do localStorage primeiro
    const saved = localStorage.getItem('notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.map((notification: any) => ({
          ...notification,
          timestamp: new Date(notification.timestamp)
        }));
      } catch (error) {
        console.error('Erro ao carregar notificações do localStorage:', error);
      }
    }
    return [];
  });
  const [loading, setLoading] = useState(true);
  const [previousCount, setPreviousCount] = useState(0);

  // Função para salvar notificações no localStorage
  const saveToLocalStorage = (notifications: Notification[]) => {
    try {
      localStorage.setItem('notifications', JSON.stringify(notifications));
    } catch (error) {
      console.error('Erro ao salvar notificações no localStorage:', error);
    }
  };

  // Carregar notificações do backend
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.log('Token não encontrado, usando notificações mockadas');
        const mockNotifications = getMockNotifications();
        setNotifications(mockNotifications);
        saveToLocalStorage(mockNotifications);
        return;
      }

      console.log('🔍 Tentando carregar notificações do backend...');
      const response = await fetch('/api/notifications', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 Status da resposta:', response.status);
      console.log('🔍 Headers da resposta:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const data = await response.json();
        console.log('Notificações carregadas do backend:', data);
        
        const notificationsWithDates = data.map((notification: any) => ({
          ...notification,
          timestamp: new Date(notification.timestamp)
        }));
        
                // Verificar se há novas notificações
        const newCount = notificationsWithDates.filter((n: Notification) => !n.read).length;
        const oldCount = notifications.filter((n: Notification) => !n.read).length;

        if (newCount > oldCount && oldCount > 0) {
            const newNotifications = newCount - oldCount;
            console.log(`🔔 ${newNotifications} nova(s) notificação(ões) recebida(s)!`);
            // Aqui você pode adicionar um toast ou som de notificação
        }

        // Sempre atualizar o contador, mesmo se não houver novas notificações
        console.log(`🔔 Contador atualizado: ${newCount} notificações não lidas`);
        
        setNotifications(notificationsWithDates);
        saveToLocalStorage(notificationsWithDates);
      } else {
        console.log('Erro na resposta do backend, usando notificações mockadas');
        // Se não houver endpoint e não há notificações salvas, usar mockadas
        if (notifications.length === 0) {
          const mockNotifications = getMockNotifications();
          setNotifications(mockNotifications);
          saveToLocalStorage(mockNotifications);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      // Fallback para notificações mockadas apenas se não há notificações salvas
      if (notifications.length === 0) {
        const mockNotifications = getMockNotifications();
        setNotifications(mockNotifications);
        saveToLocalStorage(mockNotifications);
      }
    } finally {
      setLoading(false);
    }
  };

  // Marcar notificação como lida
  const markAsRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setNotifications(prev => {
          const updated = prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, read: true }
              : notification
          );
          saveToLocalStorage(updated);
          return updated;
        });
      } else {
        // Se não houver endpoint, marcar localmente
        setNotifications(prev => {
          const updated = prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, read: true }
              : notification
          );
          saveToLocalStorage(updated);
          return updated;
        });
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      // Fallback: marcar localmente mesmo com erro
      setNotifications(prev => {
        const updated = prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        );
        saveToLocalStorage(updated);
        return updated;
      });
    }
  };

  // Marcar todas como lidas
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/notifications/mark-all-read', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setNotifications(prev => {
          const updated = prev.map(notification => ({ ...notification, read: true }));
          saveToLocalStorage(updated);
          return updated;
        });
      } else {
        // Se não houver endpoint, marcar localmente
        setNotifications(prev => {
          const updated = prev.map(notification => ({ ...notification, read: true }));
          saveToLocalStorage(updated);
          return updated;
        });
      }
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
      // Fallback: marcar localmente mesmo com erro
      setNotifications(prev => {
        const updated = prev.map(notification => ({ ...notification, read: true }));
        saveToLocalStorage(updated);
        return updated;
      });
    }
  };

  // Adicionar nova notificação
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false
    };
    
    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      saveToLocalStorage(updated);
      return updated;
    });
  };

  // Notificações mockadas para desenvolvimento
  const getMockNotifications = (): Notification[] => [
    {
      id: '1',
      title: 'Sincronização concluída',
      message: '77 usuários foram sincronizados do Google Workspace com sucesso',
      type: 'success',
      timestamp: new Date(Date.now() - 5 * 60 * 1000), // 5 minutos atrás
      read: false
    },
    {
      id: '2',
      title: 'Template atualizado',
      message: 'O template "Assinatura Padrão" foi atualizado pelo sistema',
      type: 'info',
      timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutos atrás
      read: false
    },
    {
      id: '3',
      title: 'Agendador executado',
      message: 'O agendador "SyncUsers" foi executado com sucesso às 17:00',
      type: 'success',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 horas atrás
      read: true
    },
    {
      id: '4',
      title: 'Nova assinatura criada',
      message: 'Uma nova assinatura foi criada para o domínio acspro.com.br',
      type: 'info',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 horas atrás
      read: true
    },
    {
      id: '5',
      title: 'Erro de sincronização',
      message: 'Falha na sincronização de fotos. Verifique as configurações.',
      type: 'error',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 horas atrás
      read: false
    }
  ];

  // Carregar notificações ao montar o hook
  useEffect(() => {
    // Se já temos notificações carregadas do localStorage, não precisa carregar novamente
    if (notifications.length === 0) {
      loadNotifications();
    } else {
      setLoading(false);
    }

                // Configurar atualização automática a cada 10 segundos
            const interval = setInterval(() => {
                console.log('🔄 Atualizando notificações automaticamente...');
                loadNotifications();
            }, 10000); // 10 segundos

    // Atualizar quando o usuário volta à aba
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Usuário voltou à aba, atualizando notificações...');
        loadNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Limpar o intervalo e event listener quando o componente for desmontado
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Contar notificações não lidas
  const unreadCount = notifications.filter(n => !n.read).length;

  // Formatar tempo relativo
  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `Há ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `Há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
  };

  // Função para forçar atualização imediata
  const refreshNotifications = useCallback(async () => {
    console.log('🔄 Forçando atualização de notificações...');
    setLoading(true);
    try {
      await loadNotifications();
      console.log('✅ Notificações atualizadas com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao atualizar notificações:', error);
    } finally {
      setLoading(false);
    }
  }, [loadNotifications]);

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    addNotification,
    loadNotifications,
    refreshNotifications,
    formatRelativeTime
  };
};
